/**
 * Browser host for the focus pipeline.
 *
 * All decision logic lives in `@evolveos/focus-core`; this hook is the adapter that supplies
 * a camera, a clock and pixel measurements, and turns the pipeline's output into UI state.
 *
 * What changed from the first implementation, and why each mattered:
 *
 * - **The loop is rate-limited.** It previously ran on `requestAnimationFrame`, inferring at
 *   the display refresh rate — roughly 60 fps, six times the configured target — which burns
 *   CPU on a laptop all day for no accuracy gain. Inference now runs at the policy's rate,
 *   adapting downward when frames get expensive (§26, §27).
 * - **Events and statistics are real.** The UI previously received a hardcoded
 *   `event_count: 0` and an empty event list, so the one thing the product exists to explain
 *   was always blank. They now come from the pipeline.
 * - **Frame quality is measured.** Without it the §20 uncertainty machinery had nothing to
 *   work with and a pitch-dark frame produced a confident reading.
 * - **Consent gates the camera.** `getUserMedia` is not called until an acknowledgement for
 *   the current policy version exists on this device.
 * - **React state is throttled.** Re-rendering the tree at the inference rate is wasted work;
 *   the snapshot is published a few times a second.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  AdaptiveRateController,
  CORE_VERSION,
  DIRECTION_LABEL,
  FocusPipeline,
  STATE_LABEL,
  SessionGuard,
  SystemClock,
  aggregateSimilarity,
  isFrontal,
  isUsableForIdentity,
  loadConsent,
  noticeDigest,
  recordConsent,
  renderNotice,
  requireConsent,
  type AttentionEvent,
  type ConsentRecord,
  type GuardDecision,
  type IdentityObservation,
  type Policy,
} from '../core/index.js';

import type { AgentConnectionState, AgentRecentEventDto, AgentStatusSnapshot } from '../types';
import { createFaceLandmarker, detectLandmarks, type LandmarkerHandle } from '../cv/faceLandmarker';
import { FrameMetricsSampler } from '../cv/frameMetrics';
import { verifyFace } from '../cv/identityClient';
import { alignFaceToJpeg } from '../cv/faceAlign';
import { AttentionEventReporter } from '../cv/attentionEventClient';
import { browserPolicy } from '../lib/policy';
import { LocalStorageConsentStore } from '../privacy/localConsentStore';

const HOST_VERSION = `${CORE_VERSION}-web`;
/** UI refresh rate. Independent of, and much slower than, the inference rate. */
const PUBLISH_INTERVAL_MS = 250;
const MAX_RECENT_EVENTS = 12;
/**
 * How often to report to the guard when the camera could not be started at all.
 *
 * Slower than the inference rate because there is nothing to infer — this only has to keep the
 * guard's clock running so a missing camera reaches its lock threshold.
 */
const HEARTBEAT_INTERVAL_MS = 1000;
/**
 * How many recent identity comparisons to keep.
 *
 * The verdict is the *median* of these, so one blurred or half-lit frame cannot cause a false
 * rejection, while a sustained run of low scores still surfaces. Taking the best of the window
 * instead — the first implementation — let the previous occupant's high scores keep reporting
 * `match` after somebody else sat down.
 */
const IDENTITY_SAMPLE_WINDOW = 5;

export interface ConsentState {
  /** True once we know consent is needed and has not been given. */
  required: boolean;
  checked: boolean;
  notice: string;
  record: ConsentRecord | null;
  error: string | undefined;
  accept: () => Promise<void>;
  revoke: () => Promise<void>;
}

export interface UseBrowserFocusPipelineResult {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  status: AgentStatusSnapshot | undefined;
  connection: AgentConnectionState;
  errorMessage: string | undefined;
  policy: Policy;
  configVersion: string;
  consent: ConsentState;
  /** Latest session-guard decision, or null when the guard is disabled. */
  guard: GuardDecision | null;
  /** Latest identity verdict, or null when no check has completed. */
  identity: IdentityObservation | null;
  /** Human-readable account of the last comparison, e.g. the similarity and threshold. */
  identityDetail: string;
  /** Clear a lock after a successful re-authentication. */
  unlock: () => void;
  /**
   * Inject an identity verdict.
   *
   * Real verdicts come from `svc-biometric-embedding` comparing a frame against the enrolled
   * template. Exposed here so the lock path can be exercised before that is wired, and so a
   * host can supply verdicts from wherever it obtains them.
   */
  setIdentity: (identity: IdentityObservation | null) => void;
}

export interface UseBrowserFocusPipelineOptions {
  /** Override the shipped session-guard policy — used by the dev harness to enforce. */
  guardOverrides?: Partial<Policy['sessionGuard']>;
  /**
   * Staff identifier to verify against. When set, the loop periodically sends one frame for
   * comparison with the enrolled template and feeds the verdict to the guard.
   *
   * Leaving it undefined disables identity checks entirely: the guard then only ever sees
   * `null`, which it treats as unverified and never locks on.
   */
  identitySubjectId?: string;
}

export function useBrowserFocusPipeline(
  enabled: boolean,
  options: UseBrowserFocusPipelineOptions = {},
): UseBrowserFocusPipelineResult {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState<AgentStatusSnapshot | undefined>();
  const [connection, setConnection] = useState<AgentConnectionState>('waiting');
  const [errorMessage, setErrorMessage] = useState<string>();

  const resolved = useMemo(() => browserPolicy(), []);
  const policy = resolved.policy;

  const store = useMemo(() => new LocalStorageConsentStore(), []);
  const notice = useMemo(
    () =>
      renderNotice({
        families: policy.privacy.collectSignals,
        retentionDays: policy.privacy.retentionDays,
        policyVersion: policy.privacy.policyVersion,
      }),
    [policy],
  );

  const [consentRecord, setConsentRecord] = useState<ConsentRecord | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState<string>();

  // --- consent -----------------------------------------------------------------------
  const checkConsent = useCallback(async () => {
    try {
      const record = await requireConsent(store, {
        policyVersion: policy.privacy.policyVersion,
        signals: policy.privacy.collectSignals,
      });
      setConsentRecord(record);
      setConsentError(undefined);
    } catch (error) {
      setConsentRecord(null);
      // Not surfaced as a page error: needing consent is a normal state, not a failure.
      setConsentError(error instanceof Error ? error.message : String(error));
    } finally {
      setConsentChecked(true);
    }
  }, [policy, store]);

  useEffect(() => {
    void checkConsent();
  }, [checkConsent]);

  const accept = useCallback(async () => {
    try {
      const existing = await loadConsent(store).catch(() => null);
      await recordConsent(store, {
        policyVersion: policy.privacy.policyVersion,
        signals: policy.privacy.collectSignals,
        agentVersion: HOST_VERSION,
        acknowledgedBy: existing?.acknowledgedBy ?? 'browser-user',
        noticeDigest: noticeDigest(notice),
      });
      await checkConsent();
    } catch (error) {
      setConsentError(error instanceof Error ? error.message : String(error));
    }
  }, [checkConsent, notice, policy, store]);

  const revoke = useCallback(async () => {
    await store.clear();
    setConsentRecord(null);
    setStatus(undefined);
    setConnection('waiting');
    await checkConsent();
  }, [checkConsent, store]);

  /**
   * Whether monitoring may start.
   *
   * `requireConsent` throws whenever no acknowledgement record exists on the device, and it does
   * not consult `privacy.requireAcknowledgement` at all — so with the in-app notice switched off
   * (the operator's decision here), nothing ever records consent, this stayed false forever, and
   * the capture effect returned before calling `getUserMedia`. The camera therefore never opened
   * after login and the guard was never fed a single observation, which is why no lock could ever
   * fire. Enrolment was unaffected because it uses its own hook with no consent gate, so the
   * symptom was "the camera works during enrolment and never again".
   *
   * When acknowledgement *is* required the record still gates monitoring, exactly as before.
   * The disclosure obligation does not disappear with the screen: it rides on the monitoring
   * policy HR distributes.
   */
  const consentGranted = policy.privacy.requireAcknowledgement ? consentRecord !== null : true;

  // --- session guard -----------------------------------------------------------------
  const guardPolicy = useMemo(
    () => ({ ...policy.sessionGuard, ...(options.guardOverrides ?? {}) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [policy, JSON.stringify(options.guardOverrides ?? {})],
  );
  const guardRef = useRef<SessionGuard | null>(null);
  /**
   * Why the camera is currently unusable, or null while it is fine.
   *
   * Held in a ref rather than state so the capture loop can read it without re-subscribing, and
   * so a camera that dies during `start()` is still visible to the heartbeat below.
   */
  const sensorFaultRef = useRef<string | null>(null);
  const identityRef = useRef<IdentityObservation | null>(null);
  const identityInFlightRef = useRef(false);
  const recentSimilaritiesRef = useRef<Array<{ similarity: number; monotonicTs: number }>>([]);
  const [guard, setGuard] = useState<GuardDecision | null>(null);
  /**
   * True while the session is locked.
   *
   * Drives release of the camera — see the capture effect. Kept as its own value rather than
   * read off `guard` inside the effect so the effect re-runs on the transition.
   */
  const locked = guard?.locked === true;
  const [identityDetail, setIdentityDetail] = useState<string>('');

  if (guardRef.current === null) guardRef.current = new SessionGuard(guardPolicy);

  const unlock = useCallback(() => {
    guardRef.current?.unlock(performance.now() / 1000);
    // Drop the stale verdict so the next check re-establishes identity from scratch rather
    // than immediately re-locking on the reading that caused the lock.
    identityRef.current = null;
    recentSimilaritiesRef.current = [];
    setIdentityDetail('');
    setGuard(null);
  }, []);

  const setIdentity = useCallback((identity: IdentityObservation | null) => {
    identityRef.current = identity;
  }, []);

  // --- capture and inference ---------------------------------------------------------
  useEffect(() => {
    // Nothing is captured while the session is locked.
    //
    // This is not an optimisation, it is what makes re-authentication work at all. The lock
    // overlay runs its own liveness check, which opens its own camera stream and its own
    // MediaPipe landmarker. With this pipeline still holding the device and still inferring on
    // every frame, the two contended: the overlay's preview came up black on some machines
    // (the webcam would not serve a second concurrent stream) and both loops crawled, because
    // two WASM landmarkers were doing full inference at once. So the employee was locked out
    // and then could not prove who they were — which sends them back to a password login and
    // makes the whole re-auth path pointless.
    //
    // Releasing here is also just correct: a locked session has nothing worth monitoring, and
    // the guard stays locked on its own until `unlock()` is called.
    if (!enabled || !consentGranted || locked) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stream: MediaStream | null = null;
    let landmarker: LandmarkerHandle | null = null;

    const clock = new SystemClock();
    const sampler = new FrameMetricsSampler();
    const health = new SensorHealthProbe();
    // Persists completed distraction and face-absence episodes. Created per pipeline run so its
    // queue cannot outlive the session it describes.
    const reporter = new AttentionEventReporter();
    const rate = new AdaptiveRateController(policy.vision);
    const pipeline = new FocusPipeline(policy, {
      clock,
      sessionId: sessionId(),
      agentVersion: HOST_VERSION,
    });
    pipeline.setConfigVersion(resolved.version);

    const recent: AgentRecentEventDto[] = [];
    const startedAt = clock.monotonic();
    let processed = 0;
    let lastPublishedAt = 0;
    /** Fields derived from the most recent tick, merged into every published snapshot. */
    let lastSnapshot: Partial<AgentStatusSnapshot> = {};

    const publish = (force = false): void => {
      const nowMs = performance.now();
      if (!force && nowMs - lastPublishedAt < PUBLISH_INTERVAL_MS) return;
      lastPublishedAt = nowMs;

      const summary = pipeline.summary();
      const stats = summary.eventStats;
      const stateTotals = summary.stateTotals as { seconds_by_state?: Record<string, number> };

      setStatus({
        monitoring: true,
        banner: 'MONITORING ACTIVE',
        agent_version: HOST_VERSION,
        demo: false,
        source: 'browser',
        session_id: summary.sessionId,
        elapsed_seconds: clock.monotonic() - startedAt,
        processed_frames: processed,
        ...lastSnapshot,
        event_stats: {
          event_count: stats.eventCount,
          total_duration_seconds: stats.totalDurationSeconds,
          average_duration_seconds: stats.averageDurationSeconds,
          longest_duration_seconds: stats.longestDurationSeconds,
          // Stays null until the observation window is long enough to be a measurement
          // rather than an extrapolation.
          events_per_hour: stats.eventsPerHour,
          observed_window_seconds: stats.observedWindowSeconds,
          mean_confidence: stats.meanConfidence,
          direction_distribution: stats.directionDistribution,
          severity_distribution: stats.severityDistribution,
        },
        state_stats: { seconds_by_state: stateTotals.seconds_by_state ?? {} },
        recent_events: [...recent],
      });
    };

    /**
     * Hand one observation to the session guard.
     *
     * Split out because it must run on ticks that produced no frame, and from the heartbeat
     * below when the loop is not running at all. A guard that is only fed on success can only
     * ever conclude that everything is fine.
     */
    const feedGuard = (
      monotonicTs: number,
      observation: { faceDetected: boolean; faceCount: number },
    ): void => {
      // `observable` is now strictly "is the console in the foreground". A backgrounded tab
      // throttles this loop and stops delivering frames, which looks exactly like an empty
      // chair, so nothing may accumulate. Camera health is reported separately.
      const observable =
        typeof document === 'undefined' || document.visibilityState === 'visible';
      const fault = sensorFaultRef.current;
      const decision = guardRef.current?.observe({
        monotonicTs,
        observable,
        sensorHealthy: fault === null,
        faceDetected: observation.faceDetected,
        faceCount: observation.faceCount,
        identity: identityRef.current,
        ...(fault === null ? {} : { sensorDetail: fault }),
      });
      if (decision !== undefined) setGuard(decision);
    };

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      const video = videoRef.current;
      if (video === null || landmarker === null) {
        // The model or the element is not ready. Keep the loop alive and keep the guard
        // informed; returning outright used to stall the whole pipeline silently.
        sensorFaultRef.current =
          landmarker === null
            ? 'the face model is not loaded, so presence cannot be established'
            : 'the camera preview element is not mounted';
        feedGuard(clock.monotonic(), { faceDetected: false, faceCount: 0 });
        if (!cancelled) timer = setTimeout(() => void tick(), rate.intervalMs);
        return;
      }

      // Health is checked before the frame is used, so a frozen stream is caught rather than
      // being re-analysed as a live picture of a present employee.
      const fault = health.check(video, stream, performance.now());
      sensorFaultRef.current = fault;

      if (fault === null) {
        const startedInference = performance.now();
        const monotonicTs = clock.monotonic();

        const metrics = sampler.sample(video);
        const { landmarks } = detectLandmarks(
          landmarker,
          video,
          monotonicTs * 1000,
          policy.vision.presenceConfidence,
          policy.camera.streamIsMirrored,
        );

        const result = pipeline.process({
          monotonicTs,
          timestampUtc: clock.nowUtc(),
          landmarks,
          metrics,
        });
        processed += 1;
        rate.record(performance.now() - startedInference);

        for (const event of result.events) recent.unshift(toRecentEvent(event));
        if (recent.length > MAX_RECENT_EVENTS) recent.length = MAX_RECENT_EVENTS;
        // The in-memory `recent` list is only for the UI and holds 12 entries; this is what
        // actually makes the log durable.
        reporter.record(result.events, {
          faceAbsentSeconds: guardRef.current?.lastDecision?.faceAbsentForSeconds,
          suspended: guardRef.current?.lastDecision?.suspended,
          sensorFault: sensorFaultRef.current,
          hidden: typeof document !== 'undefined' && document.visibilityState !== 'visible',
        });

        const inference = result.evaluation.inference;
        lastSnapshot = {
          state: inference.state,
          state_label: STATE_LABEL[inference.state],
          direction: inference.direction,
          direction_label: DIRECTION_LABEL[inference.direction],
          confidence: inference.confidence,
          state_duration_seconds: inference.stateDurationSeconds,
          pose:
            inference.smoothedPose === null
              ? null
              : { ...inference.smoothedPose, confidence: result.observation.poseConfidence },
          face_detected: result.observation.faceDetected,
          face_count: result.observation.faceCount,
          quality: {
            brightness: result.observation.quality.brightness,
            blur_score: result.observation.quality.blurVariance,
            flags: [...result.observation.quality.flags],
            face_absent: !result.observation.faceDetected,
          },
          evidence: [...inference.evidence],
        };
        // --- periodic identity verification -----------------------------------------
        // One frame per interval, not a stream: enough to notice a substitution, while
        // keeping the amount of imagery leaving the device to a minimum.
        const subjectId = options.identitySubjectId;
        // Only compare near-frontal frames. The embedding service crops by bounding box with
        // no landmark alignment, so its embeddings shift markedly with head angle; comparing an
        // off-angle frame against a frontal template reads as a different person. An off-angle
        // frame is not evidence about identity, so we simply do not ask.
        const frontal = isFrontal(result.observation.headPose, policy.enrolment);
        // Frame quality gates the question as well as the answer. Without this an obscured or
        // very dark frame still produced a detectable face, whose embedding was noise, which
        // scored below threshold and locked the session as `identity_mismatch` — telling the
        // employee somebody else was at their desk when they had simply covered the lens.
        // `multiple_faces` is disqualifying too: the nearest face is the one measured, so with
        // two people in shot a confident match answers the wrong question.
        const usable = isUsableForIdentity(result.observation.quality);
        if (
          subjectId !== undefined &&
          !identityInFlightRef.current &&
          result.observation.faceDetected &&
          frontal &&
          usable &&
          guardRef.current?.identityCheckDue(monotonicTs, identityRef.current) === true
        ) {
          identityInFlightRef.current = true;
          void (async () => {
            try {
              // Send an ArcFace-aligned tile, not a raw frame. Comparing a loosely-cropped
              // face against a template built the same way is what made every comparison
              // meaningless; alignment is what makes the two sides comparable.
              if (landmarks === null) return;
              const aligned = await alignFaceToJpeg(video, landmarks, {
                streamIsMirrored: policy.camera.streamIsMirrored,
              });
              if (aligned === null) return;
              const pose = result.observation.headPose;
              const lastDecision = guardRef.current?.lastDecision ?? null;
              const verdict = await verifyFace(aligned.blob, {
                faceCount: result.observation.faceCount,
                ...(pose === null ? {} : { yaw: pose.yaw, pitch: pose.pitch }),
                brightness: result.observation.quality.brightness,
                blur: result.observation.quality.blurVariance,
                ...(lastDecision === null
                  ? {}
                  : {
                      guardMismatchSeconds: lastDecision.identityMismatchForSeconds,
                      guardLocked: lastDecision.locked,
                    }),
              });

              // Best-of-N, not last-of-one. A single frame can be blurred, half-lit or caught
              // mid-blink, and one such frame must not be enough to accuse somebody of not
              // being themselves. The most favourable recent comparison is the fair one: it
              // takes a sustained run of poor scores to produce a mismatch.
              const checkedAt = clock.monotonic();
              if (verdict.similarity !== null) {
                recentSimilaritiesRef.current = [
                  ...recentSimilaritiesRef.current,
                  { similarity: verdict.similarity, monotonicTs: checkedAt },
                ].slice(-IDENTITY_SAMPLE_WINDOW);
              }

              const aggregate = aggregateSimilarity(recentSimilaritiesRef.current, {
                now: checkedAt,
                maxAgeSeconds: guardPolicy.identityResultMaxAgeSeconds,
              });
              // The **server's** threshold, never the client's. These were two different
              // numbers: raising `WORK_FOCUS_MIN_MATCH_SIMILARITY` to 0.47 on the server had no
              // effect on locking, because this comparison used the shipped policy default of
              // 0.36 and so overrode every `mismatch` the server had returned. The server owns
              // the operating point; the client only smooths the samples it is given.
              const threshold = verdict.minMatchSimilarity ?? guardPolicy.minMatchSimilarity;
              const effective =
                verdict.verdict === 'unverified' || aggregate.value === null
                  ? verdict.verdict
                  : aggregate.value >= threshold
                    ? 'match'
                    : 'mismatch';

              identityRef.current = {
                verdict: effective,
                similarity: aggregate.value ?? verdict.similarity,
                checkedAtMonotonic: checkedAt,
              };
              setIdentityDetail(
                aggregate.value === null
                  ? verdict.detail
                  : `latest ${(aggregate.latest ?? 0).toFixed(3)} · median of ${aggregate.sampleCount} = ` +
                    `${aggregate.value.toFixed(3)} · threshold ${threshold.toFixed(3)}`,
              );
            } catch {
              // A failed check is not evidence about who is present; leave the previous
              // verdict to age out rather than inventing a mismatch.
            } finally {
              identityInFlightRef.current = false;
            }
          })();
        }

        // --- session guard ----------------------------------------------------------
        feedGuard(monotonicTs, {
          faceDetected: result.observation.faceDetected,
          faceCount: result.observation.faceCount,
        });

        publish(result.events.length > 0);
      } else {
        // No usable frame this tick. Previously the whole body was skipped, so the guard was
        // never told — which is the bypass that made "switch the webcam off" a way to never be
        // locked out. The guard is fed here instead, with the reason, and decides for itself
        // whether a foreground window with a dead camera warrants a lock.
        feedGuard(clock.monotonic(), { faceDetected: false, faceCount: 0 });
        publish();
      }

      if (!cancelled) timer = setTimeout(() => void tick(), rate.intervalMs);
    };

    const start = async (): Promise<void> => {
      try {
        setConnection('waiting');
        setErrorMessage(undefined);

        const video = await waitForVideoElement(videoRef);
        if (cancelled) return;

        if (navigator.mediaDevices?.getUserMedia === undefined) {
          throw new Error('This browser does not support camera access (getUserMedia).');
        }

        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: policy.camera.width },
            height: { ideal: policy.camera.height },
            frameRate: { ideal: policy.camera.captureFps },
            facingMode: 'user',
          },
          audio: false,
        });
        if (cancelled) return;

        video.srcObject = stream;
        video.playsInline = true;
        video.muted = true;
        await video.play();
        await waitForVideoReady(video);
        if (cancelled) return;

        landmarker = await createFaceLandmarker(policy.vision);
        if (cancelled) {
          landmarker.close();
          return;
        }

        setConnection('connected');
        timer = setTimeout(() => void tick(), 0);
      } catch (error) {
        if (cancelled) return;
        setConnection('error');
        setErrorMessage(friendlyMediaError(error));
        stream?.getTracks().forEach((track) => track.stop());

        // Keep reporting to the guard even though the pipeline never started. Without this the
        // most direct bypass of all remained open: deny the camera permission, or unplug the
        // webcam before logging in, and the loop never runs, the guard never hears anything,
        // and the console stays unlocked indefinitely. A monitoring control has to fail closed
        // when its sensor is missing, not fall silent.
        sensorFaultRef.current = friendlyMediaError(error);
        const beat = (): void => {
          if (cancelled) return;
          feedGuard(clock.monotonic(), { faceDetected: false, faceCount: 0 });
          timer = setTimeout(beat, HEARTBEAT_INTERVAL_MS);
        };
        beat();
      }
    };

    void start();

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
      // Close any in-progress episode so it is not silently lost — dropping open runs
      // systematically under-reports the longest ones. The closed episodes are then queued and
      // sent, so the longest absence of a session is not the one that never gets recorded.
      reporter.record(pipeline.flush());
      void reporter.flush();
      sampler.dispose();
      landmarker?.close();
      stream?.getTracks().forEach((track) => track.stop());
      if (videoRef.current !== null) videoRef.current.srcObject = null;
    };
    // `locked` is a dependency so the camera is released the moment a lock is applied and
    // reacquired after a successful unlock. The SessionGuard itself lives in a ref, so tearing
    // this effect down does not lose the lock state that put us here.
  }, [consentGranted, enabled, locked, policy, resolved.version]);

  return {
    videoRef,
    status,
    connection: consentGranted ? connection : 'waiting',
    errorMessage,
    policy,
    configVersion: resolved.version,
    guard,
    identity: identityRef.current,
    identityDetail,
    unlock,
    setIdentity,
    consent: {
      // Only ask when policy actually requires it, otherwise a host rendering a consent screen
      // on this flag would show one the operator explicitly chose not to have.
      required: policy.privacy.requireAcknowledgement && consentChecked && !consentGranted,
      checked: consentChecked,
      notice,
      record: consentRecord,
      error: consentError,
      accept,
      revoke,
    },
  };
}

/**
 * Track whether the camera is actually producing new pictures.
 *
 * A stalled stream is the subtle case, and the dangerous one. When a webcam is switched off at
 * the OS level — or its device is disabled, or another application seizes it — the MediaStream
 * track ends but the `<video>` element keeps displaying its **last decoded frame**. `readyState`
 * stays at HAVE_ENOUGH_DATA and `videoWidth` stays non-zero, so a naive loop happily runs face
 * detection over that frozen picture and reports the employee present, forever. Checking only
 * `readyState` would therefore have left the exact bypass this class exists to close.
 *
 * `currentTime` is the reliable signal: it advances only as new media is presented, so a value
 * that stops moving means no new pictures regardless of what the element claims about itself.
 */
class SensorHealthProbe {
  private lastMediaTime = -1;
  private lastAdvancedAtMs = 0;
  /** Grace before a non-advancing clock counts as stalled, in seconds of wall time. */
  private readonly stallSeconds: number;

  constructor(stallSeconds = 2) {
    this.stallSeconds = stallSeconds;
  }

  /** Returns a description of the fault, or null when the camera is healthy. */
  check(video: HTMLVideoElement, stream: MediaStream | null, nowMs: number): string | null {
    const track = stream?.getVideoTracks()[0];
    if (stream === null || track === undefined) {
      return 'no camera stream is attached';
    }
    if (track.readyState === 'ended') {
      return 'the camera stream ended (the device was switched off, disabled or unplugged)';
    }
    // `muted` on a track means the source is not currently producing data — typically another
    // application has taken the camera, or the OS privacy shutter is closed.
    if (track.muted) {
      return 'the camera is not producing frames (in use by another application, or muted by the system)';
    }
    if (track.enabled === false) {
      return 'the camera track was disabled';
    }
    if (video.videoWidth === 0 || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      return 'the camera has not delivered a decodable frame';
    }

    const mediaTime = video.currentTime;
    if (mediaTime > this.lastMediaTime) {
      this.lastMediaTime = mediaTime;
      this.lastAdvancedAtMs = nowMs;
      return null;
    }
    // First observation: nothing to compare against yet, so give it the benefit of the doubt.
    if (this.lastAdvancedAtMs === 0) {
      this.lastAdvancedAtMs = nowMs;
      return null;
    }
    if ((nowMs - this.lastAdvancedAtMs) / 1000 >= this.stallSeconds) {
      return 'the camera stream is frozen — the picture has not advanced, so presence cannot be established';
    }
    return null;
  }

  reset(): void {
    this.lastMediaTime = -1;
    this.lastAdvancedAtMs = 0;
  }
}

function toRecentEvent(event: AttentionEvent): AgentRecentEventDto {
  return {
    type: event.eventType,
    direction: event.direction,
    severity: event.severity,
    duration_seconds: event.durationSeconds,
    evidence: [...event.evidence],
    start_time: event.startTimeUtc.toISOString(),
  };
}

function sessionId(): string {
  const cryptoObject = globalThis.crypto as { randomUUID?: () => string } | undefined;
  return cryptoObject?.randomUUID?.() ?? `browser-${Date.now().toString(36)}`;
}

async function waitForVideoElement(
  ref: React.RefObject<HTMLVideoElement | null>,
  attempts = 60,
): Promise<HTMLVideoElement> {
  for (let index = 0; index < attempts; index += 1) {
    if (ref.current !== null) return ref.current;
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve());
    });
  }
  throw new Error('Camera preview could not mount — reload the page and try again');
}

async function waitForVideoReady(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0) return;
  await new Promise<void>((resolve, reject) => {
    const cleanup = (): void => {
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('error', onError);
    };
    const onReady = (): void => {
      cleanup();
      resolve();
    };
    const onError = (): void => {
      cleanup();
      reject(
        new Error(
          'Could not start the video source — check camera permission and close other apps ' +
            'using the webcam',
        ),
      );
    };
    video.addEventListener('loadeddata', onReady);
    video.addEventListener('error', onError);
  });
}

function friendlyMediaError(error: unknown): string {
  if (error instanceof DOMException) {
    switch (error.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return (
          'Camera permission was denied. Allow camera access for this site in the browser ' +
          'address bar, then reload. The application cannot and does not bypass this ' +
          'permission.'
        );
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return 'No camera was found on this device.';
      case 'NotReadableError':
      case 'TrackStartError':
        return 'The camera is in use by another application. Close it, then reload.';
      default:
        return error.message || error.name;
    }
  }
  if (error instanceof Error) return error.message;
  return 'Could not access the camera or load the face model in this browser';
}
